import { useTheme } from '@emotion/react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import Button from 'shopper/components/Button';
import Flex from 'shopper/components/Flex';
import Tag from 'shopper/components/Tag';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import Image from 'components/Image';

import { sendEvent } from 'lib/gtag';

import { useTopbar } from 'providers/TopbarProvider';
import { useUser } from 'providers/UserProvider';

import TopbarSearch from '../TopbarSearch';
import NotificationButton from './NotificationButton';

import APP from 'constants/app';
import LINKS from 'constants/links';
import TOPBAR from 'constants/topbar';
import Z_INDEX_SCALE from 'constants/zIndexScale';

const FeaturedLandingLink = dynamic(() => import('./FeaturedLandingLink'), {
  ssr: true,
});
const LoggedUserButton = dynamic(() => import('./LoggedUserButton'), {
  ssr: false,
});
const SignUpButton = dynamic(() => import('../SignUpButton'), {
  ssr: false,
});

const MAIN_BUTTONS_LIST = [
  {
    href: LINKS.HOME,
    text: TOPBAR.TOP.MENU.OFFERS,
    gaEventAction: 'topbar_offer_tab',
    testSelectorAttr: 'offer-tab',
  },
  {
    href: LINKS.COUPONS,
    text: TOPBAR.TOP.MENU.COUPONS,
    gaEventAction: 'topbar_coupon_tab',
    testSelectorAttr: 'coupons-tab',
  },
  {
    href: LINKS.FORUM,
    text: TOPBAR.TOP.MENU.FORUM,
    gaEventAction: 'topbar_forum_tab',
    testSelectorAttr: 'forum-tab',
  },
  {
    href: LINKS.BLOG,
    text: TOPBAR.TOP.MENU.BLOG,
    gaEventAction: 'topbar_blog_tab',
    testSelectorAttr: 'blog-tab',
  },
  {
    href: LINKS.TRAVEL,
    text: TOPBAR.TOP.MENU.TRAVEL,
    gaEventAction: 'topbar_travel_tab',
    testSelectorAttr: 'travel-tab',
  },
];

const TopbarMain = ({
  gaEventCategory,
  couponsCount,
  threadsCount,
  onCouponsClick,
  onForumClick,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { pathname } = useRouter();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const { featuredLanding } = useTopbar();
  const [searchQuery, setSearchQuery] = useState(null);

  useEffect(() => {
    setSearchQuery(router.query.q);
  }, [router.query]);

  const onLogoClickHandler = (e) => {
    if (pathname === LINKS.HOME) {
      e.preventDefault();
      window.location.reload();
    }
  };

  return (
    <Flex
      borderBottom="1px solid"
      position="relative"
      borderColor={theme.app.topbar.borderBg}
      bg={theme.app.topbar.background}
      zIndex={Z_INDEX_SCALE.SIZE_5}
      full
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mx="auto"
        px={2}
        py={2}
        width={[null, null, '100%', '64rem', '80rem']}
      >
        <NextLink href={LINKS.HOME} prefetch={false} passHref>
          <Anchor onClick={onLogoClickHandler}>
            <Image
              alt="Promobit"
              loading={null}
              width={180}
              height={33}
              src={`${APP.LOCAL_IMAGE_PATH}/features/topbar/promobit-logo.svg`}
              priority
            />
          </Anchor>
        </NextLink>
        <Flex flex="2" as="nav" ml={3}>
          {MAIN_BUTTONS_LIST.map(
            ({ gaEventAction, href, text, testSelectorAttr }) => {
              const isForumButton = text === TOPBAR.TOP.MENU.FORUM;
              const isCouponsButton = text === TOPBAR.TOP.MENU.COUPONS;
              const newsCount =
                (isCouponsButton && couponsCount) ||
                (isForumButton && threadsCount) ||
                null;

              return (
                <NextLink key={href} href={href} prefetch={false} passHref>
                  <Button
                    as="a"
                    data-test-selector={testSelectorAttr}
                    type="ghost"
                    size="size2"
                    display={
                      TOPBAR.TOP.MENU.TRAVEL === text
                        ? ['none', null, null, null, 'flex']
                        : null
                    }
                    ml={1}
                    onClick={(e) => {
                      sendEvent({
                        action: gaEventAction,
                        category: gaEventCategory,
                      });

                      if (isCouponsButton) {
                        onCouponsClick(e);
                      }

                      if (isForumButton) {
                        onForumClick(e);
                      }
                    }}
                  >
                    <Text size="size2" bold upper>
                      {text}
                    </Text>
                    {newsCount && newsCount > 0 && (
                      <Tag size="size4" type="alert" ml={2} px={2}>
                        {newsCount}
                      </Tag>
                    )}
                  </Button>
                </NextLink>
              );
            }
          )}
          {featuredLanding && (
            <FeaturedLandingLink
              landingName={featuredLanding.landingName}
              landingSlug={featuredLanding.landingSlug}
              landingType={featuredLanding.landingType}
            />
          )}
        </Flex>
        <TopbarSearch searchQuery={searchQuery} />
        <NotificationButton gaEventCategory={gaEventCategory} />
        {!loadingLoggedInUser && (
          <>
            {isLoggedIn ? (
              <LoggedUserButton gaEventCategory={gaEventCategory} />
            ) : (
              <SignUpButton gaEventCategory={gaEventCategory} ml={2} />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};

TopbarMain.defaultProps = {
  couponsCount: null,
  threadsCount: null,
  onCouponsClick: () => {},
  onForumClick: () => {},
};

TopbarMain.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  couponsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  threadsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onCouponsClick: PropTypes.func,
  onForumClick: PropTypes.func,
};

export default TopbarMain;
